import { HeaderBar } from 'components/HeaderBar';
import PageLayout from 'components/PageLayout';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useAuthState } from 'state/useAuthState';
import SupportUsIcon from 'assets/header-heart.svg?react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { useProgressionModel } from 'features/wildfires/hooks/useProgressionModel';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { ProgressionModelMap } from 'features/wildfires/components/ProgressionModelMap';
import { ProgressionModelDrawerContent } from 'features/wildfires/components/ProgressionModelDrawerContent';
import {
  DrawerRefContent,
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  MapEntityDrawer,
} from 'components/Map/MapEntityDrawer';
import { ProgressionModelSlider } from 'features/wildfires/components/ProgressionModelSlider';
import { useEffect, useRef } from 'react';
import { MapLayers } from 'components/Map/constants';
import { ProgressionModelDialog } from 'features/wildfires/components/ProgressionModelDialog';
import useSearchDrawerState from 'state/useSearchDrawerState';
import { useEnableLayersWithAutoReset } from 'hooks/useEnableLayersWithAutoReset';
import { useProgressionModelsEnabled } from 'features/wildfires/hooks/useProgressionModelsEnabled';

type ProgressionModelContentProps = {
  geoEventId: number;
  modelId: number;
};

const MAP_DRAWER_ENTITY_SNAP_POINTS = [
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
  0.47,
  0.7,
];

const ProgressionModelContent = (
  props: ProgressionModelContentProps,
): JSX.Element => {
  const { geoEventId, modelId } = props;
  const { open: searchDrawerOpen, setOpen: setSearchDrawerOpen } =
    useSearchDrawerState();
  const drawerRef = useRef<DrawerRefContent | null>(null);
  const searchDrawerOpenRef = useRef(searchDrawerOpen);

  useEnableLayersWithAutoReset([
    MapLayers.EVACUATION_ZONES,
    MapLayers.SURFACE_WIND,
  ]);

  /**
   * This is important to correctly position the POI dialog within the map.
   * We save a reference to the initial open value using a ref so it won't
   * change on re-renders and restore this value when the component unmounts.
   */
  useEffect(() => {
    setSearchDrawerOpen(false);
    const initialOpenValue = searchDrawerOpenRef.current;
    return () => {
      setSearchDrawerOpen(initialOpenValue);
    };
  }, [setSearchDrawerOpen]);

  return (
    <>
      <ProgressionModelMap modelId={modelId} drawerRef={drawerRef} />
      <MapEntityDrawer
        snapPointsPct={MAP_DRAWER_ENTITY_SNAP_POINTS}
        defaultSnapPointPct={MAP_DRAWER_ENTITY_SNAP_POINTS[1]}
        open
        dismissible={false}
        preserveDrawerHeight={false}
        entityId={`progression-model-${modelId}`}
        ref={drawerRef}
      >
        <ProgressionModelDrawerContent
          geoEventId={geoEventId}
          modelId={modelId}
        />
      </MapEntityDrawer>
      <ProgressionModelDialog>
        <ProgressionModelSlider modelId={modelId} />
      </ProgressionModelDialog>
    </>
  );
};

export const WildfireProgressionModel = (
  props: RouteComponentProps<{ geoEventId: string; modelId: string }>,
): JSX.Element => {
  const {
    match: { params },
  } = props;
  const { t } = useTranslation();
  const { showSupportUs } = useAuthState();
  const showProgressionModels = useProgressionModelsEnabled();

  const geoEventId = parseInt(params.geoEventId, 10);
  const modelId = parseInt(params.modelId, 10);

  const { isLoading, isError, progressionModel } = useProgressionModel({
    modelId,
  });

  if (!showProgressionModels) {
    return <Redirect to={`/i/${params.geoEventId}`} />;
  }

  const iconActions = showSupportUs
    ? [
        {
          label: t('header.actions.supportUs'),
          to: '/support_us',
          Icon: <SupportUsIcon />,
        },
      ]
    : undefined;

  if (isLoading || isError || !progressionModel) {
    return (
      <PageLayout data-cname="WildfireProgressionModel">
        {{
          content: <LoadingAndErrors isLoading={isLoading} />,
          headerBar: <HeaderBar action="back" dark iconActions={iconActions} />,
        }}
      </PageLayout>
    );
  }

  return (
    <PageLayout data-cname="WildfireProgressionModel">
      {{
        content: (
          <>
            <Helmet>
              <title>
                {t('wildfireProgressionModel.title', {
                  name: progressionModel.sourceAttribution,
                })}
              </title>
            </Helmet>
            <ProgressionModelContent
              geoEventId={geoEventId}
              modelId={modelId}
            />
          </>
        ),
        headerBar: (
          <HeaderBar
            action={`/i/${params.geoEventId}?tab=info`}
            dark
            iconActions={iconActions}
          />
        ),
      }}
    </PageLayout>
  );
};
